import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { VolunteerEvent } from '@/api/models/volunteerModels';
import { i18n } from '@/utils/i18n';

import ListEmptyComponent from '../ListEmptyComponent';
import NextPageFetchingIndicator from '../NextPageFetchingIndicator';
import VolunteerCard from './VolunteerCard';

interface VolunteerListProps {
  volunteerEvents: VolunteerEvent[];
  location: {
    latitude: number;
    longitude: number;
  } | null;
  canEdit: boolean;
  onEndReached: () => void;
  refetch: () => void;
  isRefetching: boolean;
  isFetchingNextPage: boolean;
}

const VolunteerList = ({
  location,
  volunteerEvents,
  canEdit,
  onEndReached,
  refetch,
  isRefetching,
  isFetchingNextPage
}: VolunteerListProps) => {
  const { width } = useWindowDimensions();

  const [numOfColumns, setNumOfColumns] = useState(3);

  useEffect(() => {
    if (width > 1200 && numOfColumns !== 3) {
      setNumOfColumns(3);
    } else if (width > 820 && width < 1200 && numOfColumns !== 2) {
      setNumOfColumns(2);
    } else if (width < 820 && numOfColumns !== 1) {
      setNumOfColumns(1);
    }
  }, [numOfColumns, width]);

  return (
    <FlatList
      //Needed to force refresh of columns on the FlatList
      key={numOfColumns}
      style={style.flatList}
      {...(numOfColumns > 1 && {
        columnWrapperStyle: style.flatListColumnWrapperStyle
      })}
      contentContainerStyle={style.flatListContentContainerStyle}
      horizontal={false}
      numColumns={numOfColumns}
      keyExtractor={(item) => item.id}
      data={volunteerEvents}
      showsVerticalScrollIndicator={false}
      onRefresh={refetch}
      refreshing={isRefetching}
      onEndReachedThreshold={0.75}
      onEndReached={onEndReached}
      renderItem={({ item }) => {
        return (
          <View style={{ flex: 1 / numOfColumns }}>
            <VolunteerCard
              canEdit={canEdit}
              volunteerEvent={item}
              userLocation={location}
              onCardPress={() => {
                router.navigate({
                  pathname: '/volunteerDetail/[volunteerId]',
                  params: {
                    volunteerId: item.id
                  }
                });
              }}
              onDelete={refetch}
            />
          </View>
        );
      }}
      ListEmptyComponent={<ListEmptyComponent label={i18n.t('npo_tab.empty_volunteer')} />}
      ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
    />
  );
};

export default VolunteerList;

const style = StyleSheet.create({
  flatList: {
    flex: 1,
    width: '100%',
    height: '100%',
    paddingBottom: 26
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    flex: 1,
    gap: 27
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    flex: 1,
    gap: 22
  }
});
